/**
 * Strategy and Strategic Driver Types
 *
 * Strategy entity with strategic drivers. Vision, Mission, and North Star
 * are authored on the Strategy Canvas; the Strategy object itself holds
 * name, description, time horizon, and linked Objectives/Goals/Cycles.
 * Part of Workstream 5: Strategy Chain
 */
import { normalizeTo100 } from '../../lib/weightAllocation';

/**
 * Time horizon for strategy
 */
export type StrategyTimeHorizon = 'quarterly' | 'annual' | 'multi-year';

/**
 * Strategy status
 */
export type StrategyStatus = 'draft' | 'active' | 'archived';

/**
 * Strategic driver - a key priority area linked to criteria
 */
export interface StrategicDriver {
  id: string;
  name: string;
  description?: string;
  
  // Weight for prioritization (should sum to 100 across all drivers)
  weight: number;
  
  // Linked criteria IDs - drivers influence how we evaluate priorities
  linkedCriteriaIds: string[];
  
  // Key results / metrics
  keyMetrics?: StrategicDriverMetric[];
  
  // Owner
  ownerId?: string;
  
  // Order for display
  order: number;
  
  // Status
  isActive: boolean;
}

/**
 * Metric associated with a strategic driver
 */
export interface StrategicDriverMetric {
  id: string;
  name: string;
  currentValue?: number;
  targetValue?: number;
  unit?: string;
  lastUpdated?: Date;
}

/**
 * Strategy entity
 */
export interface Strategy {
  id: string;
  name: string;
  description?: string;

  // Strategic drivers
  strategicDrivers: StrategicDriver[];
  
  // Scope
  timeHorizon: StrategyTimeHorizon;
  orgUnitId?: string;
  orgUnitName?: string;
  
  // Ownership
  ownerId: string;
  ownerName?: string;
  stakeholderIds: string[];
  
  // Status
  status: StrategyStatus;
  
  // Effective period
  effectiveFrom?: Date;
  effectiveUntil?: Date;
  
  // Linked cycles
  linkedCycleIds: string[];
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  
  // Workspace scoping
  workspaceId?: string;
}

/**
 * Strategy summary for lists
 */
export interface StrategySummary {
  id: string;
  name: string;
  status: StrategyStatus;
  timeHorizon: StrategyTimeHorizon;
  driverCount: number;
  linkedCycleCount: number;
  ownerName?: string;
  effectiveFrom?: Date;
  effectiveUntil?: Date;
}

/**
 * Create strategy input
 */
export interface CreateStrategyInput {
  name: string;
  description?: string;
  timeHorizon: StrategyTimeHorizon;
  orgUnitId?: string;
  ownerId: string;
  stakeholderIds?: string[];
  effectiveFrom?: Date;
  effectiveUntil?: Date;
  /** Defaults to the current workspace. */
  workspaceId?: string;
}

/**
 * Update strategy input
 */
export interface UpdateStrategyInput {
  name?: string;
  description?: string;
  timeHorizon?: StrategyTimeHorizon;
  orgUnitId?: string;
  ownerId?: string;
  stakeholderIds?: string[];
  status?: StrategyStatus;
  effectiveFrom?: Date;
  effectiveUntil?: Date;
}

/**
 * Create strategic driver input
 */
export interface CreateStrategicDriverInput {
  strategyId: string;
  name: string;
  description?: string;
  weight: number;
  linkedCriteriaIds?: string[];
  ownerId?: string;
}

/**
 * Update strategic driver input
 */
export interface UpdateStrategicDriverInput {
  name?: string;
  description?: string;
  weight?: number;
  linkedCriteriaIds?: string[];
  ownerId?: string;
  isActive?: boolean;
  order?: number;
}

/**
 * Strategy alignment check result
 */
export interface StrategyAlignmentCheck {
  strategyId: string;
  cycleId: string;
  
  // Overall alignment
  alignmentScore: number; // 0-100
  
  // Driver coverage
  driverCoverage: Array<{
    driverId: string;
    driverName: string;
    itemCount: number;
    itemPercentage: number;
    topItems: Array<{ id: string; title: string }>;
  }>;
  
  // Gaps
  uncoveredDrivers: string[];
  itemsNotAligned: Array<{ id: string; title: string }>;
  
  // Recommendations
  recommendations: string[];
}

/**
 * Strategy filter options
 */
export interface StrategyFilters {
  status?: StrategyStatus[];
  timeHorizon?: StrategyTimeHorizon[];
  orgUnitId?: string;
  ownerId?: string;
  effectiveAsOf?: Date;
}

// Helper functions

/**
 * Get display label for time horizon
 */
export function getTimeHorizonLabel(horizon: StrategyTimeHorizon): string {
  const labels: Record<StrategyTimeHorizon, string> = {
    quarterly: 'Quarterly',
    annual: 'Annual',
    'multi-year': 'Multi-Year',
  };
  return labels[horizon];
}

/**
 * Get display label for strategy status
 */
export function getStrategyStatusLabel(status: StrategyStatus): string {
  const labels: Record<StrategyStatus, string> = {
    draft: 'Draft',
    active: 'Active',
    archived: 'Archived',
  };
  return labels[status];
}

/**
 * Get color for strategy status
 */
export function getStrategyStatusColor(status: StrategyStatus): string {
  const colors: Record<StrategyStatus, string> = {
    draft: 'gray',
    active: 'green',
    archived: 'red',
  };
  return colors[status];
}

/**
 * Check if strategy is currently effective
 */
export function isStrategyEffective(strategy: Strategy): boolean {
  const now = new Date();
  
  if (strategy.status !== 'active') return false;
  if (strategy.effectiveFrom && strategy.effectiveFrom > now) return false;
  if (strategy.effectiveUntil && strategy.effectiveUntil < now) return false;
  
  return true;
}

/**
 * Calculate total driver weight
 */
export function calculateTotalDriverWeight(drivers: StrategicDriver[]): number {
  return drivers.filter(d => d.isActive).reduce((sum, d) => sum + d.weight, 0);
}

/**
 * Validate driver weights sum to 100
 */
export function validateDriverWeights(drivers: StrategicDriver[]): boolean {
  const total = calculateTotalDriverWeight(drivers);
  return total === 100;
}

/**
 * Normalize active driver weights to whole points summing to exactly 100
 * (shared allocator — the old per-row Math.round could land on 99 or 101).
 */
export function normalizeDriverWeights(drivers: StrategicDriver[]): StrategicDriver[] {
  const total = calculateTotalDriverWeight(drivers);
  if (total === 0 || total === 100) return drivers;
  const shares = normalizeTo100(
    Object.fromEntries(drivers.map((d, i) => [String(i), d.isActive ? d.weight : 0])),
  );
  return drivers.map((d, i) => (d.isActive ? { ...d, weight: shares[String(i)] } : d));
}

/**
 * Sort drivers by order
 */
export function sortDriversByOrder(drivers: StrategicDriver[]): StrategicDriver[] {
  return [...drivers].sort((a, b) => a.order - b.order);
}

/**
 * Create empty strategy
 */
export function createEmptyStrategy(ownerId: string): Omit<Strategy, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    name: '',
    description: '',
    strategicDrivers: [],
    timeHorizon: 'annual',
    ownerId,
    stakeholderIds: [],
    status: 'draft',
    linkedCycleIds: [],
  };
}

/**
 * Create empty strategic driver
 */
export function createEmptyDriver(order: number): Omit<StrategicDriver, 'id'> {
  return {
    name: '',
    description: '',
    weight: 0,
    linkedCriteriaIds: [],
    order,
    isActive: true,
  };
}
